import {
  formatAttachmentSize,
  type AttachmentRow,
} from '../../../api/attachments'
import { PexIcon } from '../../../components/icons/PexIcon'
import { useT } from '../../../i18n'
import { messagingI18n } from '../../../i18n/messaging'
import {
  classifyAttachment,
  isVerifiedMessagingVoice,
} from './messaging-media'
import { VoiceMessagePlayer } from './VoiceMessagePlayer'

type MessageAttachmentsProps = {
  attachmentIds: string[]
  rowsById: Map<string, AttachmentRow>
  unavailable: boolean
  loading?: boolean
  appearance?: 'default' | 'messenger'
}

export function MessageAttachments({
  attachmentIds,
  rowsById,
  unavailable,
  loading = false,
  appearance = 'default',
}: MessageAttachmentsProps) {
  const t = useT(messagingI18n)

  const unavailableItem = (id: string) => (
    <li
      key={id}
      className={`flex items-start gap-2 rounded-[var(--list-control-radius)] border px-2.5 py-2 text-xs ${
        appearance === 'messenger'
          ? 'border-white/10 bg-white/5 text-white/55'
          : 'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]'
      }`}
    >
      <PexIcon name="packageUnavailable" size="sm" tone="warning" decorative />
      <span>
        <span
          className={
            appearance === 'messenger'
              ? 'block font-semibold text-white'
              : 'block font-semibold text-[var(--color-text)]'
          }
        >
          {t.attachments.unavailable}
        </span>
        <span className="block text-[11px]">
          {t.attachments.unavailableDescription}
        </span>
      </span>
    </li>
  )

  if (attachmentIds.length === 0) return null

  if (loading) {
    return (
      <p className="mt-1.5 text-xs text-[var(--color-text-muted)]" role="status">
        {t.attachments.loading}
      </p>
    )
  }

  return (
    <ul
      data-appearance={appearance}
      className={
        appearance === 'messenger'
          ? 'mt-1.5 flex flex-wrap gap-1.5'
          : 'mt-1.5 space-y-1.5'
      }
      aria-label={t.attachments.messageFiles}
    >
      {attachmentIds.map((id) => {
        const row = unavailable ? undefined : rowsById.get(id)
        if (!row) {
          return unavailableItem(id)
        }

        const classifiedKind = classifyAttachment(row)
        const mediaKind = isVerifiedMessagingVoice(row)
          ? 'voice'
          : classifiedKind === 'voice'
            ? 'document'
            : row.role.trim().toLowerCase() === classifiedKind
              ? classifiedKind
              : 'document'

        if (mediaKind === 'photo') {
          const previewUrl = row.thumbnail_url ?? row.download_url
          if (!previewUrl) return unavailableItem(id)

          const image = (
            <img
              src={previewUrl}
              alt={t.media.photoAttachmentLabel(row.original_filename)}
              loading="lazy"
              decoding="async"
              className="aspect-square h-full w-full object-cover"
            />
          )
          const frameClass = `block size-28 overflow-hidden rounded-[var(--list-control-radius)] border sm:size-32 ${
            appearance === 'messenger'
              ? 'border-white/10 bg-white/5'
              : 'border-[var(--color-border)] bg-[var(--color-surface-muted)]'
          }`

          return (
            <li key={id} className="max-w-full">
              {row.download_url ? (
                <a
                  href={row.download_url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${frameClass} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)]`}
                >
                  {image}
                </a>
              ) : (
                <div className={frameClass}>{image}</div>
              )}
            </li>
          )
        }

        if (mediaKind === 'voice') {
          if (!row.download_url) return unavailableItem(id)

          return (
            <li key={id} className="min-w-0 max-w-full">
              <VoiceMessagePlayer
                src={row.download_url}
                label={t.media.voiceAttachmentLabel}
                appearance={appearance}
                downloadUrl={row.download_url}
              />
            </li>
          )
        }

        const content = (
          <>
            <PexIcon name="documents" size="sm" tone="muted" decorative />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">
                {row.original_filename}
              </span>
              <span className={appearance === 'messenger' ? 'block text-[11px] text-white/55' : 'block text-[11px] text-[var(--color-text-muted)]'}>
                {row.content_type} · {formatAttachmentSize(row.file_size)}
              </span>
            </span>
          </>
        )

        return (
          <li key={id}>
            {row.download_url ? (
              <a
                href={row.download_url}
                target="_blank"
                rel="noreferrer"
                className={`flex min-h-11 items-center gap-2 rounded-[var(--list-control-radius)] border px-2.5 py-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus)] ${
                  appearance === 'messenger'
                    ? 'border-white/10 bg-white/5 text-white hover:border-white/25'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)] hover:border-[var(--color-action)]'
                }`}
              >
                {content}
              </a>
            ) : (
              <div
                className={`flex min-h-11 items-center gap-2 rounded-[var(--list-control-radius)] border px-2.5 py-2 text-xs ${
                  appearance === 'messenger'
                    ? 'border-white/10 bg-white/5 text-white'
                    : 'border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text)]'
                }`}
              >
                {content}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
